<template>
  <v-card id="org-users" class="vms-surface" elevation="0">
    <v-toolbar flat class="px-4 pt-4 pb-2">
      <v-toolbar-title v-if="!showSearch" class="vms-page-title">
        Organization Users
      </v-toolbar-title>

      <v-text-field v-else v-model="filterBySearch" prepend-icon="mdi-magnify" append-outer-icon="mdi-close"
        @click:append-outer="{ showSearch = !showSearch }" max-width="400px">
        <v-icon>
          mdi-close
        </v-icon>
      </v-text-field>
      <v-spacer></v-spacer>

      <VmsColumnVisibility
        v-model="selectedHeaders"
        :headers="headers"
        @change="toggleColumnVisibility"
        class="mr-3"
      />

      <v-tooltip left>
        <template v-slot:activator="{ on, attrs }">
          <v-hover v-slot="{ hover }">
            <v-btn :class="{ 'blue--text': hover }" icon v-bind="attrs" v-on="on"
              v-on:click="showSearch = !showSearch">
              <v-icon>mdi-magnify</v-icon>
            </v-btn>
          </v-hover>
        </template>
        <span class="text-caption">Search</span>
      </v-tooltip>

      <v-tooltip left>
        <template v-slot:activator="{ on, attrs }">
          <v-hover v-slot="{ hover }">
            <v-btn :class="{ 'info--text' : hover }" icon v-bind="attrs" v-on="on" @click="importUsers">
              <v-icon color="teal">mdi-file-import</v-icon>
            </v-btn>
          </v-hover>
        </template>
        <span class="text-caption">Import Users</span>
      </v-tooltip>

      <v-tooltip left>
        <template v-slot:activator="{ on, attrs }">
          <v-hover v-slot="{ hover }">
            <v-btn :class="{ 'info--text' : hover }" icon v-bind="attrs" v-on="on" @click="addUser">
              <v-icon color="info">mdi-account-plus</v-icon>
            </v-btn>
          </v-hover>
        </template>
        <span class="text-caption">Add User</span>
      </v-tooltip>
    </v-toolbar>

    <VmsResponsiveList :items="organizationUsers" :loading="loading" loading-text="Loading users…">
      <template #table>
        <v-data-table 
          :headers="computedHeaders" 
          :items="organizationUsers" 
          :server-items-length="organizationUsersPagination?.total"
          :options.sync="options"
          :loading="loading"
          loading-text="Loading... Please wait"
        >
          <template v-slot:[`item.index`]="{ index }">
            {{ ++index }}
          </template>

          <template v-slot:[`item.roles`]="{ item }">

            <div v-if="(item.relationships.roles[0]).length > 0" class="py-2">
              <v-tooltip top v-for="(role,index) in item.relationships.roles[0]" :key="`user-role-${item.id}-${role.id || role?.attributes?.id || index}`">

                <template v-slot:activator="{ on, attrs }">
                  <v-hover v-slot="{ hover }">
                    <v-chip :class="{
                      'grey' : hover , 'green white--text' : !role.attributes.thru_date
                    }" class="ma-1" v-bind="attrs" v-on="on">
                      {{ role.relationships.role[0]['role_name'] }}
                    </v-chip>
                  </v-hover>
                </template>

                <v-simple-table class="transparent white--text">
                  <template v-slot:default>
                    <thead>
                    <tr border="bottom" color="red">
                      <th class="text-left white--text">
                        Role
                      </th>
                      <th class="text-center white--text">
                        From
                      </th>
                      <th class="text-center white--text">
                        Status
                      </th>
                    </tr>
                    </thead>
                    <tbody>
                    <tr :key="role.id">
                      <td>{{ role.relationships.role[0]['role_name'] }}</td>
                      <td>{{ role.attributes.from_date }}</td>
                      <td>{{ role.attributes.thru_date ? 'Inactive' : 'Active' }}</td>
                    </tr>
                    </tbody>
                  </template>
                </v-simple-table>

              </v-tooltip>
            </div>

            <VmsIconAction v-else icon="mdi-account-tie-hat" label="Assign role/s" @click="assignRole(item)" />
          </template>

          <template v-slot:[`item.relationships.organization.from_date`]="{ item }">
            {{ formatDate(item.relationships.organization.from_date) }}
          </template>

          <template v-slot:[`item.relationships.organization.to_date`]="{ item }">
            {{ formatDate(item.relationships.organization.to_date) }}
          </template>

          <template v-slot:[`item.edit`]="{ item }">
            <VmsIconAction icon="mdi-pencil-outline" label="Edit" @click="editUser(item)" />
          </template>

          <template v-slot:[`item.delete`]="{ item }">
            <VmsIconAction icon="mdi-delete-outline" label="Delete" color="error" @click="deleteItem(item)" />
          </template>
        </v-data-table>
      </template>

      <template #card="{ item }">
        <div class="vms-list-card__title">
          {{ item.attributes.firstname }} {{ item.attributes.lastname }}
        </div>
        <div class="vms-list-card__meta" v-if="(item.relationships.roles[0]).length > 0">
          <v-chip
            v-for="(role, index) in item.relationships.roles[0]"
            :key="`user-chip-role-${item.id}-${role.id || role?.attributes?.id || index}`"
            small
            :color="!role.attributes.thru_date ? 'green' : 'grey'"
            :class="!role.attributes.thru_date ? 'white--text' : ''"
          >
            {{ role.relationships.role[0]['role_name'] }}
          </v-chip>
        </div>
        <div class="vms-list-card__row">
          <span class="vms-list-card__label">From</span>
          <span>{{ formatDate(item.relationships.organization.from_date) }}</span>
        </div>
        <div class="vms-list-card__row">
          <span class="vms-list-card__label">To</span>
          <span>{{ formatDate(item.relationships.organization.to_date) }}</span>
        </div>
        <div class="vms-list-card__actions">
          <VmsIconAction v-if="(item.relationships.roles[0]).length === 0" icon="mdi-account-tie-hat" label="Assign role/s" @click="assignRole(item)" />
          <VmsIconAction icon="mdi-pencil-outline" label="Edit" @click="editUser(item)" />
          <VmsIconAction icon="mdi-delete-outline" label="Delete" color="error" @click="deleteItem(item)" />
        </div>
      </template>

      <template #empty>
        <div class="vms-empty">
          <v-icon large color="grey lighten-1">mdi-account-group</v-icon>
          <p class="mt-2 grey--text px-4">No Organization Users</p>
        </div>
      </template>
    </VmsResponsiveList>

    <v-row justify="center">
      <v-dialog v-model="showUserModal" max-width="600" scrollable>
        <v-card>
          <v-card-title class="grey--text text-h5">
            User
          </v-card-title>

          <v-divider></v-divider>

          <v-card-text class="mb-0 pb-0">
            <v-form class="pt-7 pb-0" ref="form" v-model="valid" lazy-validation>
              <v-text-field dense v-model="user.name" label="Full Name" :error-messages="apiErrors.name" outlined>
              </v-text-field>
              <div class="pb-2">
                <template>
                  <vue-phone-number-input
                    v-model="phoneNumber" 
                    @update="updatePhoneNumber"
                    :default-country-code="national_number" 
                    size="md"
                  ></vue-phone-number-input>
                  <span class="text-caption pl-4 red--text">{{ apiErrors.phone ? apiErrors.phone[0] : '' }}</span>
                  <span class="text-caption red--text" v-if="phoneValidationError">{{ phoneValidationError }}</span>
                </template>
              </div>
              <v-text-field 
                dense 
                v-model="user.email" 
                label="Email" 
                :error-messages="apiErrors.email" outlined
              ></v-text-field>
              <v-text-field 
                dense 
                v-model="user.id_number" 
                label="ID No" 
                :error-messages="apiErrors.id_number" 
                outlined
                :rules="userRules.required"
              ></v-text-field>
              <v-select 
                v-model="user.user_organizations" 
                :items="organizations"
                item-text="legal_name" 
                item-value="id" 
                chips
                label="Organization(s)" 
                multiple 
                :error-messages="apiErrors.user_organizations" 
                outlined
                :rules="userRules.select"
              ></v-select>
              <v-select 
                v-model="user.user_roles" 
                :items="roles"
                item-text="role_name" 
                item-value="id" 
                attach 
                chips
                label="Role(s)" 
                multiple 
                :error-messages="apiErrors.user_roles" 
                outlined
              ></v-select>
              <v-radio-group v-model="user.is_admin" class="mt-0" v-if="user.name">
                <template v-slot:label>
                  <div>Is {{ user.name != null ? user.name : 'organization' }} an organization <strong>admin?</strong></div>
                </template>
                <v-radio v-bind:value=1>
                  <template v-slot:label>
                    <div>Yes, {{ user.name }} is an admin.</div>
                  </template>
                </v-radio>
                <v-radio v-bind:value=0>
                  <template v-slot:label>
                    <div>No, {{ user.name }} is not an admin.</div>
                  </template>
                </v-radio>
              </v-radio-group>
              <v-text-field 
                dense 
                v-model="user.password" 
                label="Password"
                placeholder="Leave blank if you don't want to reset the user's password"
                :error-messages="apiErrors.password" 
                outlined
                type="password"
                v-if="checkIfWatchman(user.user_roles)"
              ></v-text-field>
              <v-text-field 
                dense 
                v-model="user.password_confirmation" 
                label="Password Confirmation"
                placeholder="Leave blank if you don't want to reset the user's password"
                :error-messages="apiErrors.password" 
                outlined
                type="password"
                v-if="checkIfWatchman(user.user_roles)"
              ></v-text-field>
            </v-form>
          </v-card-text>

          <v-divider></v-divider>

          <v-card-actions>
            <v-btn 
              color="red darken-1" 
              text 
              @click="closeModal" 
              class="text-capitalize mx-1"
            >
              Cancel
            </v-btn>
            <v-spacer></v-spacer>
            <v-btn 
              v-if="editUserModal" color="green darken-1 white--text"
              @click="saveUserUpdates" 
              class="text-capitalize mx-1"
              :loading="submitting"
            >
              Update
            </v-btn>
            <v-btn 
              v-else 
              color="green darken-1 white--text" 
              @click="saveUser" 
              class="text-capitalize mx-1"
              :loading="submitting"
            >
              Save
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </v-row>

    <v-row justify="center">
      <v-dialog v-model="showDeleteDialog" persistent width="400">
        <v-card>
          <v-card-title class="headline">
            Proceed with caution!
            <span class="float-right ml-1">
              <v-icon class="mdi mdi-alert-circle-outline"></v-icon>
            </span>
          </v-card-title>
          <v-card-text>
            You are about to delete a user,
            are you sure you want to delete this item?
          </v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn color="green darken-1" text @click="closeModal">Cancel</v-btn>
            <v-btn color="error" text @click="deleteUser">Delete</v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </v-row>

    <v-dialog 
      v-model="assignRoleModal" 
      persistent 
      max-width="600px"
      :fullscreen="$vuetify.breakpoint.xsOnly" 
      scrollable
    >
      <v-card>
        <v-card-title class="grey--text text-h5">
          User Roles
        </v-card-title>

        <v-divider></v-divider>

        <v-form ref="form1" v-model="valid" lazy-validation>
          <v-container>
            <v-text-field 
              v-model="user.name" 
              label="Full Name" 
              :error-messages="apiErrors.name" 
              class="mx-1"
              readonly 
              dense
              outlined
            ></v-text-field>
            <v-autocomplete
              :items="organizations"
              label="Organization"
              dense
              outlined
              item-text="legal_name"
              item-value="id"
              v-model="user.organization_id"
              color="info"
              class="mx-1"
              :error-messages="apiErrors.organization_id" 
            ></v-autocomplete>
            <v-select 
              v-model="user.user_roles" 
              :items="roles" 
              item-text="role_name"
              item-value="id" 
              attach 
              chips 
              label="Assign Role/s" 
              multiple
              :error-messages="apiErrors.user_roles" 
              class="mx-1"
              dense
              readonly
              outlined
            ></v-select>
          </v-container>
        </v-form>

        <v-divider></v-divider>

        <v-card-actions>
          <v-btn color="red darken-1" text @click="closeModal">
            Cancel
          </v-btn>
          <v-spacer></v-spacer>
          <v-btn color="green darken-1 white--text" @click="saveAssignUserRoles" :loading="submitting">
            Save
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog 
      v-model="showImportModal" 
      persistent 
      max-width="600px"
      :fullscreen="$vuetify.breakpoint.xsOnly" 
      scrollable
    >
      <v-card>
        <v-card-title class="grey--text text-h5">
          Import Users
        </v-card-title>

        <v-divider></v-divider>

        <v-form ref="form2" v-model="valid" lazy-validation>
          <v-container>
            <v-col cols="12" md="12" sm="12" class="text-center">
              <v-btn block text color="primary" @click="getUsersImportTemplate">
                <v-icon color="teal">mdi-import</v-icon>
                <span class="text--primary">Click here to download the Users Import Template</span>
              </v-btn>
            </v-col>
            <v-col cols="12" md="12" sm="12">
              <v-file-input
                v-model="usersImportFile"
                color="primary"
                counter
                label="File input"
                placeholder="Select your Users Import File"
                prepend-icon="mdi-paperclip"
                outlined
                dense
                :show-size="1000"
                accept=".csv, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel"
                :rules="rules"
                class="mx-1"
              >
                <template v-slot:selection="{ index, text }">
                  <v-chip
                    v-if="index < 2"
                    color="deep-purple accent-4"
                    dark
                    label
                    small
                  >
                    {{ text }}
                  </v-chip>

                  <span
                    v-else-if="index === 2"
                    class="text-overline grey--text text--darken-3 mx-2"
                  >
                    +{{ usersImportFile.length - 2 }} File(s)
                  </span>
                </template>
              </v-file-input>
            </v-col>
            <v-col cols="12" md="12" sm="12" v-if="apiErrors && apiErrors.length > 0" class="mt-n6">
              <v-list subheader>
                <v-subheader>Errors</v-subheader>

                <v-list-item
                  v-for="(error, index) in apiErrors"
                  :key="`user-api-error-${index}`"
                >
                  <v-list-item-icon>
                    <v-icon color="error">
                      mdi-alert-circle-outline
                    </v-icon>
                  </v-list-item-icon>

                  <v-list-item-content>
                    <v-list-item-title>{{ error[0] }}</v-list-item-title>
                  </v-list-item-content>
                </v-list-item>
              </v-list>
            </v-col>
          </v-container>
        </v-form>

        <v-divider></v-divider>

        <v-card-actions>
          <v-btn color="red darken-1" text @click="closeModal">
            Cancel
          </v-btn>
          <v-spacer></v-spacer>
          <v-btn color="green darken-1 white--text" @click="saveImport" :loading="submitting">
            Save
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-card>
</template>

<script>
import User from '../../models/user';
import OrganizationService from '../../services/organization.service'
import rolesService from '../../services/roles.service';
import UserService from '../../services/user.service';
import moment from 'moment';
import VmsResponsiveList from '../../components/VmsResponsiveList.vue';
import VmsIconAction from '../../components/VmsIconAction.vue';
import VmsColumnVisibility from '../../components/VmsColumnVisibility.vue';
import columnVisibility from '../../mixins/columnVisibility';

export default {
  name: "Users",
  components: { VmsResponsiveList, VmsIconAction, VmsColumnVisibility },
  mixins: [columnVisibility],
  props: [
    'organization'
  ],
  data() {
    return {
      user: new User(),
      filterBySearch: null,
      organizationUsers: [],
      organizationUsersPagination: {},
      submitting: false,
      loading: false,
      usersImportFile: null,
      rules: [
        value => !value || value.size < 10000000 || 'Import size should be less than 10 MB!',
      ],
      headers: [
        {
          text: "#",
          align: "start",
          filterable: true,
          value: "index",
          visible: true,
        }, 
        {
          text: "First Name",
          align: "start",
          filterable: true,
          value: "attributes.firstname",
          visible: true,
        }, 
        {
          text: "Last Name",
          align: "start",
          filterable: true,
          value: "attributes.lastname",
          visible: true,
        }, 
        {
          text: "Role",
          align: "center",
          filterable: false,
          value: "roles",
          visible: true,
        }, 
        {
          text: "From Date",
          align: "start",
          filterable: true,
          value: "relationships.organization.from_date",
          visible: false,
        },
        {
          text: "Edit",
          align: "center",
          filterable: false,
          value: "edit",
          visible: true,
          sticky: true,
        }, 
        {
          text: "Delete",
          align: "center",
          filterable: false,
          value: "delete",
          visible: true,
          sticky: true,
        }
      ],
      showUserModal: false,
      roles: [],
      apiErrors: [],
      showSearch: null,
      editUserModal: false,
      assignRoleModal: false,
      national_number: "KE",
      phoneNumber: null,
      phoneValidationError: null,
      phoneErrorColor: "blue",
      showDeleteDialog: false,
      organizations: [],
      options: {},
      showImportModal: false,
      valid: false,
      userRules: {
        select: [(v) => !!v || "An item should be selected"],
        select2: [(v) => v.length > 0 || "Atleast one item should be selected"],
        required: [
          (v) => !!v || "Field is required",
          (v) => (v && v.length >= 4) || "Min 4 characters",
        ],
        file: [
          (v) => !!v || "File is required",
          (v) => (v && v.length > 0) || "File is required",
        ],
      },
    }
  },
  created() {
    this.initColumnVisibility();
    this.$nextTick(() => {
      this.filterBySearch = null;
      this.getUsers();
      this.getOrganizations();
      this.getRoles();
    });
  },
  computed: {
    organizationUsersWithIndex() {
      return this.organizationUsers.map(
          (organizationUsers, index) => ({
            ...organizationUsers,
            index: index + 1
          })
      )
    },
    watchmanRole() {
      let roleId = 0;
      let index = -1;
      index = this.roles?.findIndex((item) => (item.role_name.toLowerCase() == 'watchman' || item.role_name.toLowerCase() == 'security'));
      if (index > -1) {
        roleId = this.roles?.[index]['id'];
      }

      return roleId;
    },
  },
  methods: {
    importUsers() {
      this.user.organization_id = this.organization.id;
      this.usersImportFile = null;
      this.showImportModal = true;
      this.apiErrors = [];
    },

    getUsersImportTemplate() {
      this.submitting = true;
      UserService.exportUsers(this.organization.id).then(
        (response) => {
          if (response.status == 200) {
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', 'users.xlsx');
            link.click();

            this.submitting = false;
          } else {
            console.log(response.data.error);
            this.submitting = false;
          }
        },
        (error) => {
          this.submitting = false;

          console.log(error);
          if (error.response.status == 401) {
            this.handleLogout();
          }
        }
      );
    },

    saveImport() {
      this.valid = this.$refs.form2.validate();
      if (this.valid && this.usersImportFile) {
        // import file
        this.submitting = true;

        UserService.importUsers(this.organization.id, { file: this.usersImportFile }).then(
          (response) => {
            if (response.status === 200) {
              this.$store.dispatch("alert/success", "Users imported successfully!");
              this.loading = false;
              this.getUsers();
              this.closeModal();
            } else {
              if (response.response?.status === 422) {
                this.apiErrors = response?.response?.data?.errors;
                this.$store.dispatch("alert/error", response.response?.data?.message);
              } else {
                this.$store.dispatch("alert/error", response.message);
              }

              this.loading = false;
            }

            this.submitting = false;
          },
          (error) => {
            this.submitting = false;

            if (error.response.status === 422) {
              this.apiErrors = error.response.data.errors;

              this.$store.dispatch(
                  "alert/error",
                  error.response.data.message
              );

              this.loading = false;
            }

            this.$store.dispatch("alert/error", error.response.data.message);
          }
        );
      }
    },
    
    formatDate(date, format = 'DD-MMM-YYYY') {
      if (date) {
        return moment(date).format(format);
      }
    },

    getOrganizations() {
      let user = JSON.parse(window.localStorage.getItem('user'));

      OrganizationService.personOrganizations(user.id).then(
        (response) => {
          if (response.status == 200) {
            this.organizations = response.data.data;
            this.loading = false;
          } else {
            console.log(response.data.error);
          }
        },
        (error) => {
          this.organizations = [];

          console.log(error);
          if (error.response.status == 401) {
            this.handleLogout();
          }
        }
      );
    },

    getUsers() {
      this.loading = true;
      OrganizationService.organization_users(this.organization.id, { options: this.options }).then(
        success => {
          this.organizationUsers = success.data.data.data;
          this.organizationUsersPagination = success.data.data;
          
          this.loading = false;
        },
        error => {
          console.log(error)
          this.organizationUsers = [];
          this.loading = false;
        }
      )
    },

    checkIfWatchman(roles) {
      if (roles) {
        return roles.includes(this.watchmanRole);
      } else {
        return false;
      }
    },
    saveUser() {
      this.valid = this.$refs.form.validate();
      if (this.valid) {
        if (this.phoneNumber === null) {
          this.phoneErrorColor = "red"
          this.phoneValidationError = "The phone number is required";
        }

        this.user.current_organization_id = this.organization.id;
        this.submitting = true;

        UserService.store(this.user).then(
          (response) => {
            if (response.status === 200) {
              this.$store.dispatch("alert/success", this.user.name + " created");
              this.loading = false;
              this.submitting = false;
              this.getUsers();
            } else {
              this.submitting = false;

              this.$store.dispatch(
                  "alert/error",
                  (this.user.name ?? "User") + " not created" + response.message
              );
            }

            this.closeModal();
          },
          (error) => {
            this.submitting = false;

            if (error.response.status === 422) {
              this.apiErrors = error.response.data.errors;
              this.$store.dispatch("alert/error", error.response.data.message);
              this.loading = false;
            }

            this.$store.dispatch(
                "alert/error",
                error.response.data.message
            );
          }
        );
      }
    },

    saveUserUpdates() {
      this.valid = this.$refs.form.validate();
      if (this.valid) {
        this.user.current_organization_id = this.organization.id;
        this.submitting = true;

        UserService.updateOrganizationUser(this.user).then(
          (response) => {
            if (response.status === 200) {
              this.$store.dispatch(
                  "alert/success",
                  this.user.name + " updated"
              );
              this.loading = false;
              this.getUsers();

            } else {
              this.$store.dispatch(
                  "alert/error",
                  (this.user.name ?? "User") + " not updated" + response.message
              );
            }

            this.submitting = false;
            this.closeModal();
          },
          (error) => {
            this.submitting = false;

            if (error.response.status === 422) {
              this.apiErrors = error.response.data.errors;

              this.$store.dispatch(
                  "alert/error",
                  error.response.data.message
              );

              this.loading = false;
            }

            this.$store.dispatch("alert/error", error.response.data.message);
          }
        );
      }
    },

    saveAssignUserRoles() {
      this.valid = this.$refs.form1.validate();
      if (this.valid) {
        this.submitting = true;
        UserService.assignOrganizationUserRoles(this.user).then(
          (response) => {
            if (response.status === 200) {
              this.$store.dispatch("alert/success", this.user.name + " updated");
              this.loading = false;
              this.getUsers();
            } else {
              this.$store.dispatch("alert/error", (this.user.name ?? "User") + " not updated" + response.message);
            }
            
            this.submitting = false;
            this.closeModal();
          },
          (error) => {
            this.submitting = false;

            if (error.response.status === 422) {
              this.apiErrors = error.response.data.errors;
              this.$store.dispatch("alert/error", error.response.data.message);
              this.loading = false;
            }

            this.$store.dispatch("alert/error", error.response.data.message);
          }
        );
      }
    },

    assignRole(user) {
      this.user.name = user.attributes.firstname + " " + user.attributes.lastname;
      this.assignRoleModal = true;
      this.user.id = user.id;
      this.user.organization_id = this.organization.id;
      this.user.user_organizations = [this.organization.id];
    },

    editUser(user) {
      this.user.id = user.id;
      this.user.name = user.attributes.firstname + " " + user.attributes.lastname;
      this.phoneNumber = user.attributes.phone;
      this.user.email = user.attributes.email;
      this.user.organization_id = this.organization.id;
      this.user.id_number = user.attributes.id_number;
      this.user.is_admin = user.attributes.is_admin;
      this.user.user_organizations = user.user_organizations;

      if ((user.relationships.roles[0]).length !== 0) {
        let user_roles = user.relationships.roles[0];
        let role_ids = [];

        user_roles.forEach(user_role => {
          role_ids.push(user_role.relationships.role[0].id)
        });

        this.user.user_roles = role_ids;
      }

      this.getRoles();
      this.editUserModal = true;
      this.showUserModal = true;
      this.apiErrors = [];
    },

    deleteItem(item) {
      this.showDeleteDialog = true;
      this.$nextTick(() => {
        this.user = Object.assign({}, item);
      });
    },

    // deleting the data from the database
    deleteUser() {
      UserService.deleteUser(this.user).then(
        (response) => {
          if (response.status === 200) {
            let index = this.organizationUsersWithIndex.indexOf(this.user);
            this.organizationUsersWithIndex.splice(index, 1)
            this.$store.dispatch("alert/success", "User deleted successfully", response.data.message);
            this.getUsers();
            this.closeModal();
          } else {
            this.$store.dispatch("alert/error", "User not deleted", response.data.message);
          }
        },
        (error) => {
          if (error.response.status === 422) {
            this.$store.dispatch("alert/error", error.response.data.message);
          }

          if (error.response.status === 401) {
            this.handleLogout();
          }

          this.$store.dispatch("alert/error", error.response.data.message);
        }
      );
    },

    addUser() {
      this.getRoles();
      this.user.organization_id = this.organization.id;
      this.user.user_organizations = [this.organization.id];
      this.showUserModal = true;
      this.apiErrors = [];
    },

    getRoles() {
      rolesService.organizationRoles(this.organization.id).then(
        success => {
          this.roles = success.data.data;
        },
        error => {
          let body = error.response.data.data
          this.apiErrors = body.errors;
        }
      );
    },

    closeModal() {
      this.user = new User()
      this.phoneNumber = null;
      this.assignRoleModal = false;
      this.editUserModal = false;
      this.showUserModal = false;
      this.apiErrors = [];
      this.showDeleteDialog = false;
      this.showImportModal = false;
      this.$nextTick(() => {
        this.user = Object.assign({}, new User());
      });
    },

    updatePhoneNumber(data) {
      this.user.phone = data.e164;
    },
  },
  watch: {
    options: {
      handler () {
        this.getUsers();
      },
      deep: true,
    },
    filterBySearch: {
      handler (val) {
        if (val?.length > 3 && typeof val !== 'undefined' && val !== null && val !== 'undefined') {
          this.options.filter = val;
          this.getUsers();
        }
      },
      immediate: true,
    },
  },
}
</script>